import { Box, Card, CardContent, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { StyledTableCell, StyledTableRow } from "Pages/AdminList/ProfessionalUserList";
import { useEffect, useState } from "react";
import AdminApi from "Service/Admin.api";
import dayjs from "dayjs";
import { Chip, keyframes, styled } from "@mui/material";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../Redux/Screen/Action";
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

export const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status?: string }>(({ theme, status }) => {
  const lowercaseStatus = status?.toLowerCase();

  let bgColor = theme.palette.grey[300];
  let textColor = theme.palette.text.primary;

  if (lowercaseStatus === "completed") {
    bgColor = theme.palette.success.main;
    textColor = theme.palette.success.contrastText;
  } else if (
    lowercaseStatus === "pending" ||
    lowercaseStatus === "initial consultation" ||
    lowercaseStatus === "initial assessment"
  ) {
    bgColor = theme.palette.warning.main;
    textColor = theme.palette.warning.contrastText;
  } else if (lowercaseStatus === "rejected") {
    bgColor = theme.palette.error.main;
    textColor = theme.palette.error.contrastText;
  }

  return {
    fontWeight: 600,
    borderRadius: theme.spacing(3),
    background: bgColor,
    color: textColor,
    animation: lowercaseStatus === "completed" ? `${pulse} 2s infinite` : undefined,
  };
});


// Dummy data
interface Order {
  id: number;
  pkCase: string;
  orderTypeName: string;
  caseTypeName: string;
  clientFullName: string;
  currentProcessingStatusName: string;
  statusLastUpdated: string;
  assignedProfessionalName: string;
}

const OrderHistory: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [ordersHistory, setOrdersHistory] = useState<Order[]>([]);
  const dispatch = useDispatch();
  useEffect(() => {
    fetchCasesByUserId();
  }, [])

  useEffect(() => {
    const normalizedQuery = searchQuery.toString().toLowerCase().trim();

    if (!normalizedQuery) {
      fetchCasesByUserId();
      return;
    }

    const filtered = ordersHistory.filter((order: any) => {
      return (
        order.clientFullName?.toLowerCase().includes(normalizedQuery) ||
        order.pkCase?.toString().toLowerCase().includes(normalizedQuery) ||
        order.orderTypeName?.toLowerCase().includes(normalizedQuery) ||
        order.caseTypeName?.toLowerCase().includes(normalizedQuery) ||
        order.currentProcessingStatusName?.toLowerCase().includes(normalizedQuery) ||
        formatDate(order.statusLastUpdated)?.toLowerCase().includes(normalizedQuery) ||
        order.assignedProfessionalName?.includes(normalizedQuery)
      );
    });

    setOrdersHistory(filtered);
  }, [searchQuery]);


  const fetchCasesByUserId = async () => {
    dispatch(startLoading(
      {
        loading: true,
        loadtext: "",
      }
    ))
    const persistedData = localStorage.getItem("persist:root");

    if (persistedData) {
      try {
        // Step 2: Parse root JSON
        const parsedRoot = JSON.parse(persistedData);

        // Step 3: Parse 'user' property which is itself a JSON string
        const userObj = JSON.parse(parsedRoot.user);

        // Step 4: Access userId (i.e. `id` inside `myInfo`)
        const userId = userObj?.myInfo?.id;
        const response = await AdminApi.OrderHistory(userId);
        dispatch(stopLoading());
        setOrdersHistory(response.data);

      } catch (error) {
        console.error("Failed to parse persisted user data:", error);
      }
    }
  };

  const formatDate = (date: string) => dayjs(date).format("DD MMM YYYY, hh:mm A");
  console.log("Orders History:", ordersHistory);

  const renderTable = () => (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      }}
    >
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ textAlign: "center" }}>Case No.</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center" }}>Order Type</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center" }}>Case Type</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center" }}>Assigned Professional</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center" }}>Status</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "center" }}>Created At</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordersHistory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6">No orders found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              ordersHistory.map((order) => (
                <StyledTableRow key={order.id}>
                  <TableCell sx={{ textAlign: "center" }}>{order.pkCase}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{order.orderTypeName}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{order.caseTypeName}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{order.assignedProfessionalName}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <StatusChip
                      label={order.currentProcessingStatusName}
                      status={order.currentProcessingStatusName}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{formatDate(order.statusLastUpdated)}</TableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderCards = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {ordersHistory.length === 0 ? (
        <Typography variant="body1" align="center">
          No orders found
        </Typography>
      ) : (
        ordersHistory.map((order) => (
          <Card key={order.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Case No: {order.pkCase}
              </Typography>
              <Typography variant="body2">
                <strong>Order Type:</strong> {order.orderTypeName}
              </Typography>
              <Typography variant="body2">
                <strong>Case Type:</strong> {order.caseTypeName}
              </Typography>
              <Typography variant="body2">
                <strong>Client:</strong> {order.assignedProfessionalName}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {order.currentProcessingStatusName}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(order.statusLastUpdated)}
              </Typography>
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );

  return (
    <Box
      p={3}
      width="100%"

    >
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
        padding: 2,
        borderRadius: 2,
        backgroundColor: theme => ColorData.primary.main,
        color: theme => ColorData.text.secondary,
      }}>
        Order History
      </Typography>
      <TextField
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search order history..."
        size="small"
        sx={{ width: { xs: '100%', sm: '300px', padding: 6 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {isMobile ? renderCards() : renderTable()}
    </Box>
  );
};

export default OrderHistory;
